import { codeFont } from '../../utils/fonts';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';
import DecimalField from '../shared/DecimalField';
import { formatQuantity } from '../../utils';

const dialogPaperProps = {
  sx: {
    borderRadius: { xs: '12px', sm: '16px' },
    m: { xs: 2, sm: 4 },
    maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
  },
};

function SupplierOrderReceiveDialog({ open, onClose, order, onSuccess, t }) {
  const { currentOrganization } = useUserStore();
  const inventoryEnabled = currentOrganization?.inventory_tracking_enabled || false;
  const [lineQuantities, setLineQuantities] = useState({});
  const [notes, setNotes] = useState('');
  const [updateInventory, setUpdateInventory] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && order?.lines) {
      const initial = {};
      order.lines.forEach((line) => {
        const remaining = parseFloat(line.remaining_quantity) || 0;
        initial[line.id] = remaining > 0 ? String(remaining) : '0';
      });
      setLineQuantities(initial);
      setNotes('');
      setError(null);
    }
  }, [open, order]);

  const handleQuantityChange = (lineId, value) => {
    setLineQuantities((prev) => ({ ...prev, [lineId]: value }));
  };

  const handleSubmit = async () => {
    const lines = order.lines
      .map((line) => ({
        order_line_id: line.id,
        quantity_received: lineQuantities[line.id] || '0',
      }))
      .filter((l) => parseFloat(l.quantity_received) > 0);

    if (lines.length === 0) {
      setError(t('purchaseOrders.receiveNoItems'));
      return;
    }

    // Validate no quantity exceeds remaining
    for (const l of lines) {
      const line = order.lines.find((ol) => ol.id === l.order_line_id);
      if (line && parseFloat(l.quantity_received) > parseFloat(line.remaining_quantity)) {
        setError(t('purchaseOrders.quantityExceedsRemaining'));
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const resp = await axios.post(
        `/api/supplier-orders/${order.id}/receive/?organization=${currentOrganization.organization_id}`,
        { lines, notes, update_inventory: updateInventory },
        { headers: { 'X-CSRFToken': getCsrfToken() } }
      );

      const receivedCount = resp.data.lines?.length || 0;
      const inventoryCount = resp.data.lines?.filter((l) => l.inventory_updated).length || 0;

      if (onSuccess) {
        onSuccess({
          receivedCount,
          inventoryCount,
          orderStatus: resp.data.order_status,
        });
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || t('purchaseOrders.receiveFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!order) return null;

  const hasRemainingItems = order.lines?.some(
    (l) => parseFloat(l.remaining_quantity) > 0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('purchaseOrders.receiveTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {order.order_reference} &mdash; {order.supplier_display_name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!hasRemainingItems && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('purchaseOrders.allItemsDelivered')}
          </Alert>
        )}

        {/* Line items */}
        <Box sx={{ mb: 2 }}>
          {order.lines?.map((line) => {
            const remaining = parseFloat(line.remaining_quantity) || 0;
            const delivered = parseFloat(line.delivered_quantity) || 0;
            const ordered = parseFloat(line.quantity) || 0;
            const isFullyDelivered = remaining <= 0;

            return (
              <Box
                key={line.id}
                sx={{
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  opacity: isFullyDelivered ? 0.5 : 1,
                }}
              >
                {/* Part info row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {line.article_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ...codeFont }}>
                      {line.article_id}
                    </Typography>
                  </Box>
                </Box>

                {/* Quantity info + input row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('purchaseOrders.ordered')}: {formatQuantity(ordered)}
                    </Typography>
                    {delivered > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('purchaseOrders.alreadyDelivered')}: {formatQuantity(delivered)}
                      </Typography>
                    )}
                  </Box>

                  {!isFullyDelivered && (
                    <Box sx={{ flex: 1, minWidth: 80, maxWidth: 120 }}>
                      <DecimalField
                        label={t('purchaseOrders.thisDelivery')}
                        value={lineQuantities[line.id] || ''}
                        onChange={(val) => handleQuantityChange(line.id, val)}
                        size="small"
                        fullWidth
                        inputProps={{ min: 0, max: remaining }}
                      />
                    </Box>
                  )}

                  {isFullyDelivered && (
                    <Chip
                      label={t('purchaseOrders.fullyDelivered')}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Notes */}
        {hasRemainingItems && (
          <TextField
            label={t('purchaseOrders.deliveryNotes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
            size="small"
          />
        )}

        {/* Inventory checkbox */}
        {inventoryEnabled && hasRemainingItems && (
          <FormControlLabel
            control={
              <Checkbox
                checked={updateInventory}
                onChange={(e) => setUpdateInventory(e.target.checked)}
                size="small"
              />
            }
            label={t('purchaseOrders.updateInventory')}
            sx={{ mt: 1.5 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        {hasRemainingItems && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : <InventoryIcon />}
          >
            {t('purchaseOrders.receiveButton')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(SupplierOrderReceiveDialog);
